// Localizes deal-risk engine signals and recommendations through machine
// codes + params from `crm.chat.deal_risk.*` catalogs. The engine's Russian
// `message` / `title` / `description` stay the LLM context and the fallback
// for payloads persisted before params and codes existed.

import type { I18n, InterpolationVars } from '@govard/i18n-core';

/** Signal shape as it arrives in structured payloads (deal_risk, cockpit). */
export interface RiskSignalLike {
  code: string;
  message: string;
  params?: Record<string, string | number> | undefined;
}

/** Recommendation shape as it arrives in structured payloads. */
export interface RiskRecommendationLike {
  code?: string | undefined;
  title: string;
  description: string;
}

export interface LocalizedRiskRecommendation {
  title: string;
  description: string;
}

/** Translators the label helpers need — matches the useTranslation() result. */
export type RiskLabelI18n = Pick<I18n, 't' | 'tc'>;

const SIGNAL_KEY_PREFIX = 'crm.chat.deal_risk.signal.';
const RECOMMENDATION_KEY_PREFIX = 'crm.chat.deal_risk.recommendation.';
const DAYS_UNIT_KEY = 'crm.chat.deal_risk.days_unit';

/**
 * Catalog params per signal code: name → expected primitive type. A signal
 * with an unknown code or with missing/ill-typed params falls back to the
 * engine message — interpolation must never render half-empty. Codes with an
 * empty expectation (missing_next_activity) localize without params.
 */
const SIGNAL_PARAM_TYPES: Record<string, Readonly<Record<string, 'string' | 'number'>>> = {
  missing_next_activity: {},
  overdue_next_activity: { days: 'number' },
  stale_activity: { days: 'number' },
  stage_age_over_norm: { stage: 'string', days: 'number', norm: 'number' },
  qualification_gaps: { gaps: 'number', total: 'number' },
  overdue_task: { title: 'string' },
};

function paramsSatisfy(code: string, params: Record<string, string | number> | undefined): boolean {
  const expected = SIGNAL_PARAM_TYPES[code];
  if (expected === undefined) return false;
  const source: Record<string, string | number> = params ?? {};
  return Object.entries(expected).every(([name, type]) => typeof source[name] === type);
}

/**
 * Localized signal text. Day counters pluralize through tc() (ru one/few/
 * many, en one/other); `stale_activity` uses the zero plural slot for the
 * "no activity recorded" branch (params.days === 0), and
 * `stage_age_over_norm` composes its two day counters from the shared unit
 * key because one tc() call drives a single count.
 */
export function dealRiskSignalText(i18n: RiskLabelI18n, signal: RiskSignalLike): string {
  const params = signal.params;
  if (!paramsSatisfy(signal.code, params)) return signal.message;
  const key = SIGNAL_KEY_PREFIX + signal.code;
  const days = params?.days;
  if (
    signal.code === 'stage_age_over_norm' &&
    typeof days === 'number' &&
    typeof params?.norm === 'number'
  ) {
    const vars: InterpolationVars = {
      ...params,
      daysWord: i18n.tc(DAYS_UNIT_KEY, days),
      normWord: i18n.tc(DAYS_UNIT_KEY, params.norm),
    };
    const translated = i18n.t(key, vars);
    return translated === key ? signal.message : translated;
  }
  if (typeof days === 'number') {
    const translated = i18n.tc(key, days, params ?? {});
    return translated === key ? signal.message : translated;
  }
  const translated = i18n.t(key, params ?? {});
  return translated === key ? signal.message : translated;
}

/**
 * Localized recommendation title/description. A missing or unknown code
 * keeps the engine-provided Russian strings — legacy payloads keep rendering
 * exactly as before.
 */
export function dealRiskRecommendationText(
  i18n: RiskLabelI18n,
  recommendation: RiskRecommendationLike,
): LocalizedRiskRecommendation {
  if (recommendation.code === undefined || recommendation.code.trim() === '') {
    return { title: recommendation.title, description: recommendation.description };
  }
  const titleKey = `${RECOMMENDATION_KEY_PREFIX}${recommendation.code}.title`;
  const descriptionKey = `${RECOMMENDATION_KEY_PREFIX}${recommendation.code}.description`;
  const title = i18n.t(titleKey);
  const description = i18n.t(descriptionKey);
  return {
    title: title === titleKey ? recommendation.title : title,
    description: description === descriptionKey ? recommendation.description : description,
  };
}
